/**
 * Notion-like block model for lesson section content.
 *
 * Sections persist a single markdown `content` string (backend/API contract
 * unchanged) — this module only parses that string into editable blocks and
 * serializes blocks back to markdown. Each block is stored as exactly one
 * physical line. serialize(parse(markdown)) round-trips byte-for-byte for ANY
 * markdown (see parseBlocks), and editing a block only rewrites that block's
 * own line — opening a section in the editor must never alter content the
 * teacher didn't touch. Inline formatting (bold/italic/etc) is handled by
 * ./marks — each block's `text` is always plain, with `marks` carrying the
 * formatting ranges; only serializeBlocks turns those into markdown tokens.
 *
 * SHARED SOURCE: copied into teacher-mobile and student-mobile by
 * scripts/sync-rich-content.js — edit here, then run that script.
 */

import { parseInline, serializeInline, sliceMarks, legacyEqToMath } from './marks.js';

export const CONTINUES_SELF = ['bullet', 'numbered', 'quote'];

let counter = 0;
export const newBlockId = () => `b${Date.now().toString(36)}${(counter++).toString(36)}`;

function detectLine(line) {
  let m;
  if ((m = line.match(/^####\s+(.*)$/))) return { type: 'h4', rest: m[1] };
  if ((m = line.match(/^###\s+(.*)$/))) return { type: 'h3', rest: m[1] };
  if ((m = line.match(/^##\s+(.*)$/))) return { type: 'h2', rest: m[1] };
  if ((m = line.match(/^#\s+(.*)$/))) return { type: 'h1', rest: m[1] };
  if ((m = line.match(/^>\s+(.*)$/))) return { type: 'quote', rest: m[1] };
  if ((m = line.match(/^[-*]\s+(.*)$/))) return { type: 'bullet', rest: m[1] };
  if ((m = line.match(/^(\d+)\.\s+(.*)$/))) return { type: 'numbered', rest: m[2], num: Number(m[1]) };
  return { type: 'p', rest: line };
}

/**
 * Besides type/text/marks, each parsed block remembers where it came from so
 * serializeBlocks can write untouched content back byte-for-byte:
 *  - `raw`: the exact source line; `orig`: its parsed type/text/marks. A block
 *    whose type/text/marks still equal `orig` is emitted as `raw` verbatim.
 *  - `sep`: the exact whitespace between the previous block and this one
 *    (single "\n" soft breaks, several blank lines, "\r" of CRLF, …); for
 *    the first block, any leading blank lines.
 *  - `num`: a numbered item's original number.
 *  - `trail` (last block only): trailing blank lines after the content.
 * Blocks created by editing (Enter, paste) carry none of these and fall back
 * to the canonical format below.
 */
export function parseBlocks(markdown) {
  const lines = (markdown ?? '').split('\n');
  const blocks = [];
  let sep = '';
  lines.forEach((line, k) => {
    if (k > 0) sep += '\n';
    if (!line.trim()) { sep += line; return; }
    const { type, rest, num } = detectLine(line);
    const { text, marks } = parseInline(rest);
    // A line still in the legacy <eq> format must never be re-emitted
    // verbatim: any save rewrites it as $…$ (see isUntouched).
    const legacy = legacyEqToMath(rest) !== rest;
    blocks.push({
      id: newBlockId(), type, text, marks, num, sep, raw: line, orig: { type, text, marks },
      ...(legacy ? { legacy: true } : {}),
    });
    sep = '';
  });
  if (blocks.length === 0) return [{ id: newBlockId(), type: 'p', text: '', marks: [] }];
  return blocks.map((b, i) => (i === blocks.length - 1 ? { ...b, trail: sep } : b));
}

const PREFIX = {
  h1: '# ', h2: '## ', h3: '### ', h4: '#### ', quote: '> ', p: '', bullet: '- ', numbered: '1. ',
};

const sameMarks = (a, b) => a.length === b.length
  && a.every((m, i) => m.kind === b[i].kind && m.start === b[i].start && m.end === b[i].end && m.param === b[i].param);

function isUntouched(b) {
  return !b.legacy && b.raw !== undefined && !!b.orig
    && b.type === b.orig.type && b.text === b.orig.text && sameMarks(b.marks, b.orig.marks);
}

function separatorBefore(b, prev) {
  // A block whose type changed (e.g. a list item demoted to a paragraph) may
  // no longer belong with its old neighbour — use the canonical separator.
  if (b.sep !== undefined && b.orig && b.type === b.orig.type) return b.sep;
  const sameGroup = !!prev && prev.type === b.type && CONTINUES_SELF.includes(b.type);
  return sameGroup ? '\n' : '\n\n';
}

export function serializeBlocks(blocks) {
  let out = '';
  let prevNumber = 0;
  blocks.forEach((b, i) => {
    const prev = blocks[i - 1];
    if (i === 0) out += b.sep ?? '';
    else out += separatorBefore(b, prev);

    let number = 0;
    if (b.type === 'numbered') {
      const keepsOwnNumber = b.num !== undefined && b.orig?.type === 'numbered';
      number = keepsOwnNumber ? b.num : (prev?.type === 'numbered' ? prevNumber + 1 : 1);
    }
    prevNumber = number;

    if (isUntouched(b)) { out += b.raw; return; }
    const raw = serializeInline(b.text, b.marks);
    out += b.type === 'numbered' ? `${number}. ${raw}` : `${PREFIX[b.type]}${raw}`;
  });
  const last = blocks[blocks.length - 1];
  return out + (last?.trail ?? '');
}

/** Parse + serialize: rewrites legacy <eq> lines as $…$ and leaves every
 * other line byte-for-byte. Used by scripts/migrate-eq-math.mjs. */
export function canonicalizeContent(markdown) {
  return serializeBlocks(parseBlocks(markdown));
}

/** Text + marks of block.text[from, to), with surrounding whitespace trimmed. */
function pieceOf(block, from, to) {
  let s = from;
  let e = to;
  while (s < e && /\s/.test(block.text[s])) s += 1;
  while (e > s && /\s/.test(block.text[e - 1])) e -= 1;
  return { text: block.text.slice(s, e), marks: sliceMarks(block.marks, s, e) };
}

/**
 * Applies a block type (heading, list, quote, paragraph) from the toolbar.
 * With a caret, or a selection spanning the whole line, the whole block
 * changes. With a partial selection only the selected words take the new
 * type: the block splits into before / selected / after, the outer pieces
 * keeping the original type. Returns the new blocks plus which block (and
 * range) to focus.
 */
export function setBlockTypeForSelection(blocks, id, type, sel) {
  const idx = blocks.findIndex((b) => b.id === id);
  if (idx === -1) return { blocks, focusId: null, selection: null };
  const block = blocks[idx];
  const start = Math.min(sel?.start ?? 0, sel?.end ?? 0);
  const end = Math.max(sel?.start ?? 0, sel?.end ?? 0);
  const before = pieceOf(block, 0, start);
  const middle = pieceOf(block, start, end);
  const after = pieceOf(block, end, block.text.length);

  const wholeBlock = !middle.text || (!before.text && !after.text) || block.type === type;
  if (wholeBlock) {
    const next = blocks.map((b, i) => (i === idx ? { ...b, type } : b));
    return { blocks: next, focusId: id, selection: sel ?? null };
  }

  const pieces = [];
  if (before.text) pieces.push({ ...block, ...before });
  const selected = before.text
    ? { id: newBlockId(), type, ...middle }
    : { ...block, type, ...middle };
  pieces.push(selected);
  if (after.text) pieces.push({ id: newBlockId(), type: block.type, ...after });

  const next = [...blocks.slice(0, idx), ...pieces, ...blocks.slice(idx + 1)];
  return { blocks: next, focusId: selected.id, selection: { start: 0, end: middle.text.length } };
}

/** 1-based position of a numbered block within its consecutive run. */
export function numberedIndex(blocks, index) {
  let n = 0;
  for (let i = index; i >= 0; i--) {
    if (blocks[i].type !== 'numbered') break;
    n++;
  }
  return n;
}

// One palette for every editor and renderer (web + both mobile apps), so a
// colour or highlight picked anywhere looks identical everywhere.
export const TEXT_COLORS = [
  { key: 'red', hex: '#EF4444' },
  { key: 'orange', hex: '#F97316' },
  { key: 'yellow', hex: '#D97706' },
  { key: 'green', hex: '#16A34A' },
  { key: 'blue', hex: '#2563EB' },
  { key: 'purple', hex: '#7C3AED' },
];
export const BG_COLORS = [
  { key: 'yellow', hex: '#FDE047' },
  { key: 'green', hex: '#86EFAC' },
  { key: 'blue', hex: '#93C5FD' },
  { key: 'pink', hex: '#FCA5A5' },
  { key: 'purple', hex: '#C4B5FD' },
];
export const TEXT_COLOR_HEX = Object.fromEntries(TEXT_COLORS.map((c) => [c.key, c.hex]));
export const BG_COLOR_HEX = Object.fromEntries(BG_COLORS.map((c) => [c.key, c.hex]));
